import React from 'react';
import { Loader2 } from 'lucide-react';
import type { Plan, CurrentSubscription } from './types';
import { buildDowngradeUsageNote, formatPagesPerSiteLabel, isUnlimitedPages } from './planChange';

export interface PaidDowngradeConfirmModalProps {
  isOpen: boolean;
  plan: Plan | null;
  currentSubscription: CurrentSubscription | null;
  processing: boolean;
  /** True while checking sites/pages against the new plan. */
  loadingPreview?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const PaidDowngradeConfirmModal: React.FC<PaidDowngradeConfirmModalProps> = ({
  isOpen,
  plan,
  currentSubscription,
  processing,
  loadingPreview = false,
  onConfirm,
  onClose,
}) => {
  if (!isOpen || !plan) return null;

  const x = plan.maxSites;
  const y = plan.maxPagesPerSite;
  const pageSummary = isUnlimitedPages(y)
    ? 'unlimited pages per site'
    : `up to ${formatPagesPerSiteLabel(y)} pages per site`;

  const usageNote = buildDowngradeUsageNote(currentSubscription, x);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Downgrade to {plan.displayName}?
        </h3>
        <p className="text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 text-sm mb-4">
          This plan allows <span className="font-semibold">{x}</span> {x === 1 ? 'site' : 'sites'}{' '}
          and <span className="font-semibold">{pageSummary}</span> (builder pages and blog posts
          count together toward the per-site cap). If you are over these limits, you will choose
          what to remove before the downgrade completes. Some paid features may no longer be
          available for new work depending on the plan.
        </p>
        {usageNote && <p className="text-sm text-gray-700 mb-4">{usageNote}</p>}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={processing || loadingPreview}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {loadingPreview || processing ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {loadingPreview ? 'Checking your sites…' : 'Processing…'}
              </span>
            ) : (
              'Confirm downgrade'
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={processing || loadingPreview}
            className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
